import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Palette,
  Sparkles,
  Users,
  ArrowRight,
  Store,
  TrendingUp,
} from "lucide-react";
import { Link } from "react-router-dom";
import heroImage from "@/assets/kente-hero.jpg";
import pattern1 from "@/assets/kente-pattern-1.jpg";
import pattern2 from "@/assets/kente-pattern-2.png";
import pattern3 from "@/assets/kente-pattern-3.png";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useState, useEffect } from "react";

const TypewriterText = () => {
  const words = ["Sash", "Bag", "Smock"];
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentText, setCurrentText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const currentWord = words[currentWordIndex];

    const timeout = setTimeout(
      () => {
        if (!isDeleting) {
          // Typing
          if (currentText.length < currentWord.length) {
            setCurrentText(currentWord.slice(0, currentText.length + 1));
          } else {
            // Finished typing, wait then start deleting
            setTimeout(() => setIsDeleting(true), 2000);
          }
        } else {
          // Deleting
          if (currentText.length > 0) {
            setCurrentText(currentText.slice(0, -1));
          } else {
            // Finished deleting, move to next word
            setIsDeleting(false);
            setCurrentWordIndex((prev) => (prev + 1) % words.length);
          }
        }
      },
      isDeleting ? 50 : 100
    );

    return () => clearTimeout(timeout);
  }, [currentText, currentWordIndex, isDeleting, words]);

  return <span>{currentText}</span>;
};

const Index = () => {
  return (
    <>
      <SEO
        title="Kente Weave Design - Custom African Graduation Stoles & Sashes"
        description="Design personalized Kente graduation stoles with authentic African patterns, custom text, and cultural symbols. Create beautiful handwoven or printed Kente sashes for your special graduation day. Order now!"
        keywords="kente, graduation stoles, african patterns, custom sashes, graduation ceremony, ghanaian culture, traditional weaving, personalized stoles, cultural symbols, adinkra symbols"
        url="https://kente-design.orctatech.com"
        type="website"
      />
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
          {/* Background with gradient overlay */}
          <div className="absolute inset-0 z-0">
            <img
              src={heroImage}
              alt="Traditional Kente cloth"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground/90 via-foreground/70 to-foreground/80" />
          </div>

          {/* Become a Weaver Button - Top Right */}
          <div className="absolute top-4 right-4 z-20">
            <Link to="/become-seller">
              <Button
                size="sm"
                className="group relative overflow-hidden bg-gradient-to-r from-primary/90 to-secondary/90 hover:from-primary hover:to-secondary text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 backdrop-blur-sm px-4 py-2"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                <TrendingUp className="mr-2 h-4 w-4 group-hover:scale-110 transition-transform duration-300" />
                <span className="font-medium">Become a Weaver</span>
              </Button>
            </Link>
          </div>

          {/* Content */}
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center space-y-8">
              <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-background leading-tight">
                Design Your Perfect{" "}
                <span className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent">
                  Kente <TypewriterText />
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-background/90 max-w-2xl mx-auto">
                Create personalized graduation stoles with authentic African
                patterns, custom text, and meaningful symbols for your special
                day.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Link to="/designer">
                  <Button variant="hero" size="lg" className="group">
                    Start Designing
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="lg"
                  className="bg-background/10 backdrop-blur-sm border-background/30 text-background hover:bg-background/20"
                >
                  Browse Gallery
                </Button>
              </div>
            </div>
          </div>

          {/* Decorative gradient */}
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent z-10" />
        </section>

        {/* Features Section */}
        <section className="py-6 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-4">
              <h2 className="text-4xl font-bold mb-4">
                Why Choose Our Kente Design?
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Authentic patterns, modern customization, and cultural pride
                combined
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <Card className="p-8 text-center hover:shadow-[var(--shadow-elevated)] transition-all border-border/50">
                <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary/60 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Palette className="h-8 w-8 text-primary-foreground" />
                </div>
                <h3 className="text-2xl font-semibold mb-4">Custom Designs</h3>
                <p className="text-muted-foreground">
                  Choose from authentic Kente patterns and personalize with your
                  own text, colors, and symbols to make it uniquely yours.
                </p>
              </Card>

              <Card className="p-8 text-center hover:shadow-[var(--shadow-elevated)] transition-all border-border/50">
                <div className="w-16 h-16 bg-gradient-to-br from-secondary to-secondary/60 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Sparkles className="h-8 w-8 text-secondary-foreground" />
                </div>
                <h3 className="text-2xl font-semibold mb-4">Premium Quality</h3>
                <p className="text-muted-foreground">
                  Work directly with skilled weavers and manufacturers who honor
                  traditional craftsmanship while ensuring modern quality.
                </p>
              </Card>

              <Card className="p-8 text-center hover:shadow-[var(--shadow-elevated)] transition-all border-border/50">
                <div className="w-16 h-16 bg-gradient-to-br from-accent to-accent/60 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="h-8 w-8 text-accent-foreground" />
                </div>
                <h3 className="text-2xl font-semibold mb-4">Group Orders</h3>
                <p className="text-muted-foreground">
                  Perfect for graduating classes and organizations. Save and
                  reorder the same design for your entire group.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Pattern Gallery Preview */}
        <section className="py-6 bg-gradient-to-b from-background to-muted/30">
          <div className="container mx-auto px-4">
            <div className="text-center mb-4">
              <h2 className="text-4xl font-bold mb-4">
                Authentic Kente Patterns
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Each pattern carries deep cultural meaning and historical
                significance
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                {
                  img: pattern1,
                  name: "Adweneasa",
                  meaning: "Creativity & Excellence",
                },
                {
                  img: pattern2,
                  name: "Ewuraba",
                  meaning: "Royalty & Heritage",
                },
                { img: pattern3, name: "Fathia", meaning: "Unity & Strength" },
              ].map((pattern, idx) => (
                <Card
                  key={idx}
                  className="overflow-hidden group cursor-pointer hover:shadow-[var(--shadow-elevated)] transition-all"
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={pattern.img}
                      alt={pattern.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold mb-2">
                      {pattern.name}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {pattern.meaning}
                    </p>
                  </div>
                </Card>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link to="/designer">
                <Button variant="default" size="lg">
                  View All Patterns
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-r from-primary via-secondary to-accent relative overflow-hidden">
          <div className="absolute inset-0 bg-[url('/src/assets/kente-pattern-1.jpg')] opacity-10 bg-repeat" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold text-background">
                Ready to Create Your Legacy?
              </h2>
              <p className="text-xl text-background/90">
                Join thousands celebrating their achievements with custom Kente
                sashes
              </p>
              <Link to="/designer">
                <Button
                  variant="outline"
                  size="lg"
                  className="bg-background text-foreground hover:bg-background/90 border-0 mt-6"
                >
                  Start Your Design Now
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default Index;
